import type { WorkflowCaseTag } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { useTranslation } from 'react-i18next'

import {
  ChakraBadge,
  HStack,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import { WorkflowCaseTagChip } from '~/components/common/WorkflowCaseTagChip'
import { WORKFLOW_CASE_NO_VALUE_LABEL } from '~/pages/WorkflowCases/models/workflowCase'

type Props = {
  tags: WorkflowCaseTag[]
  /** 折りたたみを開始する表示件数 (AC-08 既定 = 先頭 2 個)。 */
  maxVisible?: number
}

/**
 * タグ列 (AC-08: 先頭 N 個 + 「+N」折りたたみ、design `.tag-list` / `.cell-tags-more` 相当)。
 *
 * design HTML はクリックで開く popover で隠れたタグを列挙するが、本実装は ecloud Tooltip
 * の hover 表示で代替する (視覚 parity は呼び出し元の独立検証に委ね、本 skill 実行では
 * skip 許可を得ているため機能的に「折りたたみ + 内容を確認できる」を満たす簡易実装を採用)。
 */
export const WorkflowCaseTagList = ({
  tags,
  maxVisible = 2,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (tags.length === 0) {
    // design (`cases.html` / `case-templates.html`) はタグ 0 件を em dash +
    // mid-emphasis で出す。 ASCII ハイフンは他の「値なし」表記
    // (`WORKFLOW_CASE_NO_VALUE_LABEL`) とも不揃いになる。
    return (
      <Text as="span" color="Text.midEmphasis">
        {WORKFLOW_CASE_NO_VALUE_LABEL}
      </Text>
    )
  }

  const visible = tags.slice(0, maxVisible)
  const hidden = tags.slice(maxVisible)

  return (
    // design: タグ列は 1 行維持 (先頭 2 個 + 「+N」)。 折り返すとタグ数で行高が
    // 変わり、一覧の行の高さが揃わなくなる。
    <HStack flexWrap="nowrap" gap={1}>
      {visible.map((tag) => (
        <WorkflowCaseTagChip
          key={tag.label}
          color={tag.color}
          label={tag.label}
        />
      ))}
      {hidden.length > 0 && (
        <Tooltip
          content={<div>{hidden.map((tag) => tag.label).join(', ')}</div>}
        >
          <span>
            {/* 「+N」 も design `.cell-tags-more` が `.case-tag` を継承する = pill。 */}
            <ChakraBadge
              borderRadius="full"
              colorPalette="gray"
              variant="subtle"
            >
              {t('common:tag.moreCount', { count: hidden.length })}
            </ChakraBadge>
          </span>
        </Tooltip>
      )}
    </HStack>
  )
}
